import Column from '@amzn/meridian/column';
import DatePicker from '@amzn/meridian/date-picker';
import Row from '@amzn/meridian/row';
import styled from '@emotion/styled';
import i18next from 'i18next';
import React, { FC, ReactNode } from 'react';
import { Input, Text, TranslatedString, TranslationFunction } from '../../../components/blocks/localization';
import { MultiColumnInfoBoxTile } from '../../../components/blocks/MultiColumnInfoBoxTile';
import LabeledField from '../../../components/composites/LabeledField';
import { SearchableAddressSelect } from '../../../components/composites/SearchableAddressSelect';
import { HelpPopover } from '../../../components/constructed';
import { PO_NUMBER_INPUT_PATTERN } from '../../../constants/customerReference';
import { Address } from '../../../redux/types';
import { ExtractionFinding } from '../../../types';
import { datePickerLocale } from '../../../utils/datePickerLocale';
import { formatDateIsoUtc, todayIsoDate } from '../../../utils/dateUtils';
import { findingKey, findingMessage } from '../poReviewFindings';
import {
    deliveryDateFieldState,
    poNumberFieldState,
    shipDateFieldState,
    shippingLocationState,
} from '../poReviewHeaderFields';
import { isDeliveryDateBeforeShipDate } from '../poReviewValidation';
import { PoReviewFindingList } from './PoReviewFindingList';
import { WarningSentence } from './WarningSentence';

// The order fields the reviewer checks before the line items: the editable PO number and ship date, the
// printed address and shipping location, and where the document came from.

const EMPTY_VALUE = '—';

const DOWNLOAD_SOURCE_DOCUMENT_BUTTON_ID = 'downloadSourceDocument';

// Renders the help popover's newline-separated lines as separate lines.
const HelpText = styled.div`
    white-space: pre-line;
`;

type LabeledControlProps = {
    label: TranslatedString;
    help?: ReactNode;
    notReadMessage?: TranslatedString;
    findings?: ReactNode;
};

const LabeledControl: FC<LabeledControlProps> = ({ label, help, notReadMessage, findings, children }) => (
    <Column width={'100%'} spacing={'small'} spacingInset={'none'}>
        {help ? (
            <Row spacing={'small'} alignmentVertical={'center'}>
                <Text type={'h200'}>{label}</Text>
                {help}
            </Row>
        ) : (
            <Text type={'h200'}>{label}</Text>
        )}
        {children}
        {notReadMessage && <WarningSentence text={notReadMessage} />}
        {findings}
    </Column>
);

export type PoReviewDetailsBoxProps = {
    t: TranslationFunction;
    onDownloadSourceDocument: () => void;
    fileName?: string;
    createdAt?: number;
    source?: string;
    shipToAddress?: string;
    shipToAddressFindings: ExtractionFinding[];
    poNumber: string;
    poNumberInError: boolean;
    poNumberNotRead?: boolean;
    poNumberRawValue?: string;
    poNumberFindings: ExtractionFinding[];
    onPoNumberChange: (value: string) => void;
    shipDate: string;
    shipDateFindings: ExtractionFinding[];
    shipDateNotRead: boolean;
    shipDateRawValue?: string;
    shipDateInPast?: boolean;
    shipDateQuotedValue?: string;
    shipDateConcernActive: boolean;
    onShipDateChange: (value: string) => void;
    deliveryDate: string;
    deliveryDateExtracted: boolean;
    deliveryDateFindings: ExtractionFinding[];
    deliveryDateRawValue?: string;
    deliveryDateInPast?: boolean;
    deliveryDateQuotedValue?: string;
    deliveryDateConcernActive: boolean;
    onDeliveryDateChange: (value: string) => void;
    shippingLocationCode?: string;
    onShippingLocationChange: (value: string) => void;
    addressMap: Record<string, Address>;
    dataTestId?: string;
};

const PoReviewDetailsBox: FC<PoReviewDetailsBoxProps> = ({
    t,
    onDownloadSourceDocument,
    fileName,
    createdAt,
    source,
    shipToAddress,
    shipToAddressFindings,
    poNumber,
    poNumberInError,
    poNumberFindings,
    onPoNumberChange,
    poNumberNotRead,
    poNumberRawValue,
    shipDate,
    shipDateFindings,
    shipDateNotRead,
    shipDateRawValue,
    shipDateInPast,
    shipDateQuotedValue,
    shipDateConcernActive,
    onShipDateChange,
    deliveryDate,
    deliveryDateExtracted,
    deliveryDateFindings,
    deliveryDateRawValue,
    deliveryDateInPast,
    deliveryDateQuotedValue,
    deliveryDateConcernActive,
    onDeliveryDateChange,
    shippingLocationCode,
    onShippingLocationChange,
    addressMap,
    dataTestId,
}: PoReviewDetailsBoxProps) => {
    const poNumberState = poNumberFieldState(t, {
        value: poNumber,
        notRead: poNumberNotRead,
        rawValue: poNumberRawValue,
        findings: poNumberFindings,
    });
    const shipDateState = shipDateFieldState(
        t,
        { value: shipDate, notRead: shipDateNotRead, rawValue: shipDateRawValue, findings: shipDateFindings },
        Boolean(shipDateInPast),
        shipDateConcernActive
    );
    const shipDateInError = !shipDate;
    const deliveryDateState = deliveryDateFieldState(
        t,
        { value: deliveryDate, rawValue: deliveryDateRawValue, findings: deliveryDateFindings },
        Boolean(deliveryDateInPast),
        deliveryDateConcernActive
    );
    const deliveryDateBeforeShipDate = isDeliveryDateBeforeShipDate({ shipDate, deliveryDate });
    const earliestRequestedDate = todayIsoDate();
    const pickerLocale = datePickerLocale(i18next.language);
    const locationState = shippingLocationState({
        printedAddress: shipToAddress,
        findings: shipToAddressFindings,
        locationCode: shippingLocationCode,
        addressMap,
    });

    return (
        <MultiColumnInfoBoxTile
            widths={['grid-6', 'grid-6']}
            title={t('purchaseOrderDetails-tileTitle')}
            actionButtons={[
                {
                    id: DOWNLOAD_SOURCE_DOCUMENT_BUTTON_ID,
                    title: t('downloadSourceDocument-buttonLabel'),
                    callback: onDownloadSourceDocument,
                    type: 'primary',
                },
            ]}
        >
            <>
                <LabeledControl
                    label={t('poNumber-fieldLabel')}
                    help={
                        <HelpPopover
                            header={t('poNumberHelp-header')}
                            dataTestId={dataTestId && `${dataTestId}.poNumberHelp`}
                        >
                            <HelpText>
                                <Text>{t('poNumberHelp-text')}</Text>
                            </HelpText>
                        </HelpPopover>
                    }
                    notReadMessage={poNumberState.notReadText}
                    findings={<PoReviewFindingList t={t} findings={poNumberState.findings} keyPrefix={'poNumber'} />}
                >
                    <Input
                        value={poNumber}
                        onChange={onPoNumberChange}
                        onBlur={() => onPoNumberChange(poNumber.trim())}
                        pattern={PO_NUMBER_INPUT_PATTERN}
                        error={poNumberInError}
                        aria-invalid={poNumberInError ? true : undefined}
                        data-testid={dataTestId && `${dataTestId}.poNumber`}
                    />
                </LabeledControl>
                <LabeledControl
                    label={t('shipDate-fieldLabel')}
                    notReadMessage={shipDateState.notReadText}
                    findings={
                        <PoReviewFindingList
                            t={t}
                            findings={shipDateState.findings}
                            keyPrefix={'shipDate'}
                            quotedValue={shipDateQuotedValue}
                        />
                    }
                >
                    <DatePicker
                        value={shipDate}
                        onChange={onShipDateChange}
                        placeholder={t('datePicker-inputPlaceholder')}
                        size={'medium'}
                        clearButton={true}
                        error={shipDateInError}
                        aria-invalid={shipDateInError ? true : undefined}
                        disabledDates={(date: string) => date < earliestRequestedDate}
                        locale={pickerLocale}
                        data-testid={dataTestId && `${dataTestId}.shipDate`}
                    />
                </LabeledControl>
                {deliveryDateExtracted && (
                    <LabeledControl
                        label={t('deliveryDate-fieldLabel')}
                        help={
                            <>
                                <Text type={'b100'}>{t('optional-text')}</Text>
                                <HelpPopover
                                    header={t('deliveryDateHelp-header')}
                                    dataTestId={dataTestId && `${dataTestId}.deliveryDateHelp`}
                                >
                                    <Text>{t('deliveryDateHelp-text')}</Text>
                                </HelpPopover>
                            </>
                        }
                        notReadMessage={
                            deliveryDateBeforeShipDate
                                ? t('deliveryDateBeforeShipDate-text')
                                : deliveryDateState.notReadText
                        }
                        findings={
                            <PoReviewFindingList
                                t={t}
                                findings={deliveryDateState.findings}
                                keyPrefix={'deliveryDate'}
                                quotedValue={deliveryDateQuotedValue}
                            />
                        }
                    >
                        <DatePicker
                            value={deliveryDate}
                            onChange={onDeliveryDateChange}
                            placeholder={t('datePicker-inputPlaceholder')}
                            size={'medium'}
                            clearButton={true}
                            error={deliveryDateBeforeShipDate}
                            aria-invalid={deliveryDateBeforeShipDate ? true : undefined}
                            disabledDates={(date: string) => date < (shipDate || earliestRequestedDate)}
                            locale={pickerLocale}
                            data-testid={dataTestId && `${dataTestId}.deliveryDate`}
                        />
                    </LabeledControl>
                )}
            </>
            <>
                <Column width={'100%'} spacing={'small'} spacingInset={'none'}>
                    <Row spacing={'small'} alignmentVertical={'center'}>
                        <Text type={'h200'}>{t('shipTo-fieldLabel')}</Text>
                        <HelpPopover
                            header={t('shipToHelp-header')}
                            dataTestId={dataTestId && `${dataTestId}.shipToHelp`}
                        >
                            <Column spacing={'small'}>
                                <Text>{t('shipToHelp-text')}</Text>
                                {/* TODO: a page to raise these requests with a prefilled template is a follow-up */}
                            </Column>
                        </HelpPopover>
                    </Row>
                    <div data-testid={dataTestId && `${dataTestId}.shippingLocation`}>
                        <SearchableAddressSelect
                            label={t('registeredShippingLocation-fieldLabel')}
                            addressMap={addressMap}
                            value={shippingLocationCode}
                            onChange={onShippingLocationChange}
                            invalid={!locationState.hasLocation}
                            dataTestId={dataTestId && `${dataTestId}.shippingLocation`}
                        />
                    </div>
                    {locationState.showsAddressNotRead && (
                        <WarningSentence
                            text={t('shippingAddressNotRead-text')}
                            dataTestId={dataTestId && `${dataTestId}.shippingAddressNotRead`}
                        />
                    )}
                    {locationState.findings.map((finding) => (
                        <WarningSentence
                            key={findingKey(finding, 'shipToAddress')}
                            text={findingMessage(t, finding, { address: shipToAddress ?? '' })}
                        />
                    ))}
                </Column>
                <LabeledField label={t('fileName-fieldLabel')} text={t.cognate(fileName ?? EMPTY_VALUE)} />
                <LabeledField
                    label={t('source-fieldLabel')}
                    text={source ? t('source', { context: source }) : t.cognate(EMPTY_VALUE)}
                />
                <LabeledField
                    label={t('createdDate-fieldLabel')}
                    text={t.cognate(createdAt ? formatDateIsoUtc(createdAt) : EMPTY_VALUE)}
                />
            </>
        </MultiColumnInfoBoxTile>
    );
};

export { PoReviewDetailsBox };
